import { useMutation, useQueryClient } from '@tanstack/react-query';

import { HttpClientError, IDefaultResponseMessage, IQuerySettings, QueryKeys, getQuerySettings } from '@dw/models/shared';
import {
  IAuthTokenResponse,
  IInitializeAuthRequest,
  IInitializeAuthResponse,
  IRequestAuthOtpRequest,
  ISignOutRequest,
  ISwitchSchoolsRequest,
  IVerifyAuthOtpRequest
} from '@dw/models/auth';
import { useAuth, useHttpClient, useSnackbar } from '@dw/hooks';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@dw/constants';

const BASE_URL = '/v1/auth';

/**
 * Shared by the sign-in and sign-out calls. The OTP endpoints are anonymous and are reached from
 * the login page itself: an auth error must never bounce the visitor to login (they are already
 * there), and the forms render the API's "wrong code" / "expired" / "too many attempts" messages
 * inline off `mutation.error` rather than as a snackbar. Sign-out shares them — a 401 while signing
 * out must not trigger a redirect loop on the way to the login page.
 */
const authFlowRequestOptions = {
  autoLogOutOnAuthError: false,
  autoShowErrorMessages: false
};

/**
 * NOTE for the session provider: `POST /v1/auth/refresh` deliberately has NO hook here. It runs as
 * a plain `fetch` inside `Session.context` (which sits above `HttpClientContextProvider`, so it
 * cannot use `useHttpClient`, and routing refresh through the authed client would recurse). That
 * plain fetch bypasses the client's `cleanJsonResponse` pass, so the refresh response — and any
 * `IAuthTokenResponse` read back out of storage — must be run through `cleanJsonResponse`
 * (`@dw/utils`) manually, otherwise `accessTokenExpiresAt` / `refreshTokenExpiresAt` arrive as ISO
 * strings rather than the `Dayjs` values the model promises.
 */

export const useInitializeAuthMutation = (settings?: IQuerySettings<IInitializeAuthResponse, IInitializeAuthRequest>) => {
    const { POST } = useHttpClient();
    const queryClient = useQueryClient();

    const initializeAuth = async (request: IInitializeAuthRequest) => {
      return await POST<IInitializeAuthResponse>(`${BASE_URL}/initialize`, request);
    }

    const querySettings = getQuerySettings(settings)
    return useMutation<IInitializeAuthResponse, HttpClientError, IInitializeAuthRequest>({
      ...querySettings,
      mutationFn: initializeAuth,
      onSuccess: (response, input) => {
        queryClient.setQueryData([QueryKeys.Self], response.user);
        // The active school on this response is built by the very same `GetDanceSchoolRequest` that
        // `GET /v1/dance-schools/{id}` runs, so seeding it here is the identical payload and saves a
        // whole round trip on every cold load. `useGetDanceSchool` is gated on this mutation
        // succeeding, so the seed always lands before that query is enabled and it never fetches.
        //
        // Seeded ONLY from a live response — never from the persisted bootstrap snapshot, whose age
        // is unknown: stamping that as fresh would suppress the reconcile for a full stale time.
        if (response.activeDanceSchoolUser?.danceSchool) {
          queryClient.setQueryData(
            [QueryKeys.DanceSchools, response.activeDanceSchoolUser.danceSchool.id],
            response.activeDanceSchoolUser.danceSchool
          );
        }
        if (querySettings.onSuccess) {
          querySettings.onSuccess(response, input)
        }
      }
    })
}

export const useRequestAuthOtpMutation = (settings?: IQuerySettings<IDefaultResponseMessage, IRequestAuthOtpRequest>) => {
  const { POST } = useHttpClient();

  const requestAuthOtp = async (request: IRequestAuthOtpRequest) => {
    return await POST<IDefaultResponseMessage>(`${BASE_URL}/otp/request`, request, undefined, authFlowRequestOptions);
  }

  const querySettings = getQuerySettings(settings)
  return useMutation<IDefaultResponseMessage, HttpClientError, IRequestAuthOtpRequest>({
    ...querySettings,
    mutationFn: requestAuthOtp
  })
}

export const useVerifyAuthOtpMutation = (settings?: IQuerySettings<IAuthTokenResponse, IVerifyAuthOtpRequest>) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();

  const verifyAuthOtp = async (request: IVerifyAuthOtpRequest) => {
    return await POST<IAuthTokenResponse>(`${BASE_URL}/otp/verify`, request, undefined, authFlowRequestOptions);
  }

  const querySettings = getQuerySettings(settings)
  return useMutation<IAuthTokenResponse, HttpClientError, IVerifyAuthOtpRequest>({
    ...querySettings,
    mutationFn: verifyAuthOtp,
    onSuccess: (response, input) => {
      // The verify response carries the mapped user, so the cache is seeded without a round trip.
      queryClient.setQueryData([QueryKeys.Self], response.user);
      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input)
      }
    }
  })
}

/**
 * Revokes the refresh token's family server side. Deliberately does NOT clear the query cache — a
 * revoke that fails still ends the session locally, so `useSignOut()` (which every sign-out goes
 * through) owns clearing the cache unconditionally.
 */
export const useSignOutMutation = (settings?: IQuerySettings<IDefaultResponseMessage, ISignOutRequest>) => {
  const { POST } = useHttpClient();

  const signOut = async (request: ISignOutRequest) => {
    return await POST<IDefaultResponseMessage>(`${BASE_URL}/sign-out`, request, undefined, authFlowRequestOptions);
  }

  const querySettings = getQuerySettings(settings)
  return useMutation<IDefaultResponseMessage, HttpClientError, ISignOutRequest>({
    ...querySettings,
    mutationFn: signOut
  })
}

export const useSwitchSchoolsMutation = (settings?: IQuerySettings<IInitializeAuthResponse, ISwitchSchoolsRequest>) => {
  const { POST } = useHttpClient();
  const queryClient = useQueryClient();
  const { setAuthDetails } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const switchSchools = async (request: ISwitchSchoolsRequest) => {
    return await POST<IInitializeAuthResponse>(`${BASE_URL}/switch-schools`, request);
  }

  const querySettings = getQuerySettings(settings)
  return useMutation<IInitializeAuthResponse, HttpClientError, ISwitchSchoolsRequest>({
    ...querySettings,
    mutationFn: switchSchools,
    onSuccess: (response, input) => {
      // `setAuthDetails` ends in `queryClient.clear()`, so it has to run BEFORE the school is
      // seeded — seeding first (as this did) put the school in the cache and then dropped it,
      // making every switch refetch a school the server had just handed us.
      setAuthDetails(response);

      // Seeded for the same reason as the school below, and it has to be here: switching while
      // ALREADY on /dashboard makes the navigate a no-op, so no route remounts, no initialize
      // re-runs, and nothing else puts `Self` back after the clear above — `useGetSelf` would
      // refetch a user this very response is carrying.
      queryClient.setQueryData([QueryKeys.Self], response.user);

      if (response.activeDanceSchoolUser?.danceSchool) {
        queryClient.setQueryData([QueryKeys.DanceSchools, response.activeDanceSchoolUser.danceSchool.id], response.activeDanceSchoolUser.danceSchool);
        showSnackbar(`Successfully switched to ${response.activeDanceSchoolUser.danceSchool.name}`);
      }

      navigate(ROUTES.Dashboard);

      if (querySettings.onSuccess) {
        querySettings.onSuccess(response, input)
      }
    }
  })
}